import { FabricObject, StaticCanvas, Textbox } from 'fabric';
import { walkObjects } from '../../canvas/utils/walk-objects';
import { buildPieceIdIndex } from '../../canvas/utils/piece-id';
import { fitToContent } from '../../text/utils/fit-to-content';

export type AttachToSide = 'right' | 'left';

export interface AttachToConfig {
  pieceId: string;
  side: AttachToSide;
  gap?: number;
}

function readAttachTo(obj: FabricObject): AttachToConfig | undefined {
  const cfg = obj.attachTo;
  if (!cfg || typeof cfg !== 'object') return undefined;
  if (typeof cfg.pieceId !== 'string') return undefined;
  if (cfg.side !== 'right' && cfg.side !== 'left') return undefined;
  return cfg;
}

function shiftBBoxLeftTo(obj: FabricObject, desiredBBoxLeft: number): void {
  const bbox = obj.getBoundingRect();
  const delta = desiredBBoxLeft - bbox.left;
  obj.set({ left: (obj.left ?? 0) + delta });
  obj.setCoords?.();
}

/** Positions `target` horizontally next to `anchor`, using their bounding boxes (origin-agnostic). */
export function attachTo(target: FabricObject, anchor: FabricObject, cfg: AttachToConfig): void {
  const gap = cfg.gap ?? 0;
  const anchorBox = anchor.getBoundingRect();
  if (cfg.side === 'right') {
    shiftBBoxLeftTo(target, anchorBox.left + anchorBox.width + gap);
  } else {
    const targetBox = target.getBoundingRect();
    shiftBBoxLeftTo(target, anchorBox.left - gap - targetBox.width);
  }
}

const hasOwnSizing = (obj: FabricObject): boolean => Boolean(obj.fitToContent || obj.autoFit || obj.wrapText);

/** Ensures textbox widths are up to date before resolving attachments. */
function fitChainTextboxes(
  configByObj: Map<FabricObject, AttachToConfig>,
  pieceIndex: Map<string, FabricObject>
): void {
  const targets = new Set<Textbox>();
  configByObj.forEach((cfg, obj) => {
    if (obj instanceof Textbox && !hasOwnSizing(obj)) targets.add(obj);
    const anchor = pieceIndex.get(cfg.pieceId);
    if (anchor instanceof Textbox && !hasOwnSizing(anchor)) targets.add(anchor);
  });
  targets.forEach(tb => fitToContent(tb));
}

/** Resolves and applies all `attachTo` relationships in dependency order. */
export function applyAllAttachTo(canvas: StaticCanvas): void {
  const configByObj = new Map<FabricObject, AttachToConfig>();
  walkObjects(canvas, obj => {
    const cfg = readAttachTo(obj);
    if (cfg) configByObj.set(obj, cfg);
  });
  if (configByObj.size === 0) return;

  const pieceIndex = buildPieceIdIndex(canvas);
  fitChainTextboxes(configByObj, pieceIndex);

  const positioned = new Set<FabricObject>();
  const onStack = new Set<FabricObject>();

  const place = (obj: FabricObject, cfg: AttachToConfig): void => {
    if (positioned.has(obj)) return;
    // Stop on cycles to avoid infinite recursion; keep current position.
    if (onStack.has(obj)) {
      console.warn(`[attach-to] cycle detected at pieceId="${obj.pieceId}"`);
      return;
    }
    onStack.add(obj);

    const anchor = pieceIndex.get(cfg.pieceId);
    if (!anchor) {
      console.warn(`[attach-to] anchor pieceId="${cfg.pieceId}" not found; skipping`);
    } else {
      const anchorCfg = configByObj.get(anchor);
      if (anchorCfg) place(anchor, anchorCfg);
      attachTo(obj, anchor, cfg);
    }

    onStack.delete(obj);
    positioned.add(obj);
  };

  configByObj.forEach((cfg, obj) => place(obj, cfg));
}
